import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Check, Plus, Loader } from 'lucide-react';
import api from '../services/api';

const ContributingArtistSelector = ({ 
    selectedArtists = [], 
    onArtistsChange,
    placeholder = "Search for contributing artists...",
    maxArtists = 10,
    disabled = false,
    className = ""
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [error, setError] = useState(null);
    const [isBatchResolving, setIsBatchResolving] = useState(false);
    const [unresolvedNames, setUnresolvedNames] = useState([]);

    const searchInputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Handle search with debouncing
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchTerm.trim().length >= 2) {
                searchArtists(searchTerm.trim());
            } else {
                setSearchResults([]);
                setShowDropdown(false);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const searchArtists = async (query) => {
        try {
            setIsSearching(true);
            setError(null);

            const response = await api.get('/contributing-artists/search', {
                params: {
                    q: query,
                    limit: 20
                }
            });

            if (response.data.success) {
                // Filter out already selected artists
                const selectedIds = selectedArtists.map(artist => artist.user_id);
                const filteredResults = response.data.data.filter(
                    artist => !selectedIds.includes(artist.user_id)
                );
                
                setSearchResults(filteredResults);
                setShowDropdown(filteredResults.length > 0);
            }
        } catch (err) {
            console.error('Error searching artists:', err);
            setError('Failed to search artists');
            setSearchResults([]);
            setShowDropdown(false);
        } finally {
            setIsSearching(false);
        }
    };

    const selectArtist = (artist) => {
        if (selectedArtists.length >= maxArtists) {
            setError(`Maximum ${maxArtists} contributing artists allowed`);
            return;
        }

        const newSelectedArtists = [...selectedArtists, artist];
        onArtistsChange(newSelectedArtists);
        
        // Clear search and close dropdown
        setSearchTerm('');
        setSearchResults([]);
        setShowDropdown(false);
        setError(null);
    };

    const removeArtist = (artistUserId) => {
        const newSelectedArtists = selectedArtists.filter(
            artist => (artist.user_id || artist.artist_user_id) !== artistUserId
        );
        onArtistsChange(newSelectedArtists);
        setError(null);
    };

    const handleInputFocus = () => {
        if (searchResults.length > 0) {
            setShowDropdown(true);
        }
    };

    // Resolve a batch of names (from a comma-separated paste or comma-by-comma typing)
    // against the artist search API, auto-adding exact (or unambiguous single-result)
    // matches. Names that can't be confidently resolved are surfaced so the user can
    // pick them manually via the normal single search below.
    const resolveAndAddNames = async (rawNames) => {
        const names = rawNames.map(n => n.trim()).filter(Boolean);
        if (names.length === 0) return;

        setIsBatchResolving(true);
        setError(null);

        let currentSelected = selectedArtists;
        const failed = [];

        for (const name of names) {
            if (currentSelected.length >= maxArtists) {
                failed.push(name);
                continue;
            }

            const alreadySelected = currentSelected.some(a =>
                (a.display_name && a.display_name.toLowerCase() === name.toLowerCase()) ||
                (a.username && a.username.toLowerCase() === name.toLowerCase())
            );
            if (alreadySelected) continue;

            let results = [];
            try {
                const response = await api.get('/contributing-artists/search', {
                    params: { q: name, limit: 20 }
                });
                if (response.data.success) results = response.data.data;
            } catch (err) {
                console.error('Error searching artists:', err);
            }

            const exactMatch = results.find(a =>
                (a.display_name && a.display_name.toLowerCase() === name.toLowerCase()) ||
                (a.username && a.username.toLowerCase() === name.toLowerCase())
            );
            const match = exactMatch || (results.length === 1 ? results[0] : null);

            if (match) {
                currentSelected = [...currentSelected, match];
            } else {
                failed.push(name);
            }
        }

        onArtistsChange(currentSelected);
        setUnresolvedNames(failed);
        if (currentSelected.length >= maxArtists && failed.length > 0) {
            setError(`Maximum ${maxArtists} contributing artists allowed`);
        }
        setIsBatchResolving(false);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        if (value.includes(',')) {
            const parts = value.split(',');
            const lastPart = parts.pop();
            resolveAndAddNames(parts);
            setSearchTerm(lastPart.replace(/^\s+/, ''));
        } else {
            setSearchTerm(value);
        }
    };

    const handleSearchPaste = (e) => {
        const pasted = e.clipboardData.getData('text');
        if (pasted.includes(',')) {
            e.preventDefault();
            resolveAndAddNames(pasted.split(','));
            setSearchTerm('');
        }
    };

    return (
        <div className={`relative ${className}`}>
            <label className="block text-sm font-medium text-gray-300 mb-2">
                Contributing Artists
                {selectedArtists.length > 0 && (
                    <span className="ml-2 text-xs text-gray-400">
                        ({selectedArtists.length}/{maxArtists})
                    </span>
                )}
            </label>

            {/* Selected Artists */}
            {selectedArtists.length > 0 && (
                <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                        {selectedArtists.map((artist, index) => (
                            <div 
                                key={artist.user_id || artist.artist_user_id}
                                className="flex items-center gap-2 bg-blue-600/20 border border-blue-600/40 rounded-full px-3 py-1 text-sm"
                            >
                                {artist.profile_picture_url ? (
                                    <img
                                        src={artist.profile_picture_url}
                                        alt={artist.display_name}
                                        width="20"
                                        height="20"
                                        className="w-5 h-5 rounded-full object-cover"
                                        style={{ background: 'transparent' }}
                                    />
                                ) : (
                                    <div className="w-5 h-5 bg-gray-600 rounded-full flex items-center justify-center">
                                        <span className="text-xs leading-none">🌊</span>
                                    </div>
                                )}
                                <span className="text-blue-200 font-medium">
                                    {artist.display_name || artist.username}
                                </span>
                                {artist.is_artist && (
                                    <span className="text-xs text-blue-300 opacity-75">Artist</span>
                                )}
                                {!disabled && (
                                    <button
                                        type="button"
                                        onClick={() => removeArtist(artist.user_id || artist.artist_user_id)}
                                        className="text-blue-200 hover:text-red-400 transition-colors"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Search Input */}
            <div className="relative" ref={dropdownRef}>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onPaste={handleSearchPaste}
                        onFocus={handleInputFocus}
                        placeholder={
                            selectedArtists.length >= maxArtists
                                ? `Maximum ${maxArtists} artists selected`
                                : placeholder
                        }
                        disabled={disabled || isBatchResolving || selectedArtists.length >= maxArtists}
                        className="w-full bg-gray-800/50 border border-gray-600 rounded-lg pl-10 pr-10 py-3 text-white placeholder-gray-400 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    {(isSearching || isBatchResolving) && (
                        <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-400 w-4 h-4 animate-spin" />
                    )}
                </div>

                {/* Search Results Dropdown */}
                {showDropdown && searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                        {searchResults.map((artist) => (
                            <button
                                key={artist.user_id}
                                type="button"
                                onClick={() => selectArtist(artist)}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-700 transition-colors border-b border-gray-700 last:border-b-0"
                            >
                                {artist.profile_picture_url ? (
                                    <img
                                        src={artist.profile_picture_url}
                                        alt={artist.display_name}
                                        width="40"
                                        height="40"
                                        className="w-10 h-10 rounded-full object-cover"
                                        style={{ background: 'transparent' }}
                                    />
                                ) : (
                                    <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                                        <span className="text-xl leading-none">🌊</span>
                                    </div>
                                )}
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-white">
                                            {artist.display_name || artist.username}
                                        </span>
                                        {artist.verified && (
                                            <Check className="w-4 h-4 text-blue-400" />
                                        )}
                                        {artist.is_artist && (
                                            <span className="text-xs bg-purple-600/20 text-purple-300 px-2 py-0.5 rounded-full">
                                                Artist
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-sm text-gray-400">
                                        @{artist.username}
                                    </div>
                                    {artist.bio && (
                                        <div className="text-xs text-gray-500 mt-1 line-clamp-1">
                                            {artist.bio}
                                        </div>
                                    )}
                                </div>
                                <Plus className="w-4 h-4 text-gray-400" />
                            </button>
                        ))}
                    </div>
                )}

                {/* No Results Message */}
                {showDropdown && searchResults.length === 0 && searchTerm.length >= 2 && !isSearching && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-50 px-4 py-3">
                        <div className="text-center text-gray-400">
                            No artists found for "{searchTerm}"
                        </div>
                    </div>
                )}
            </div>

            {/* Error Message */}
            {error && (
                <div className="mt-2 text-red-400 text-sm">
                    {error}
                </div>
            )}

            {/* Unresolved Names from comma-separated entry */}
            {unresolvedNames.length > 0 && (
                <div className="mt-2 flex items-start gap-2 text-amber-400 text-sm bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                    <span className="flex-1">
                        Couldn't find a unique match for: {unresolvedNames.join(', ')}. Try searching for {unresolvedNames.length > 1 ? 'them' : 'it'} individually above.
                    </span>
                    <button
                        type="button"
                        onClick={() => setUnresolvedNames([])}
                        className="text-amber-300 hover:text-white flex-shrink-0"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            )}

            {/* Help Text */}
            {!error && unresolvedNames.length === 0 && selectedArtists.length === 0 && (
                <div className="mt-2 text-gray-500 text-xs">
                    Search and select users individually, or paste/type multiple names separated by commas (e.g. "Harley Galeano, Jeff Lloyd, Mat Huntsberger") to add them all at once.
                </div>
            )}
        </div>
    );
};

export default ContributingArtistSelector;